import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ defaultService = "Allgemeine Anfrage" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", vehicle: "",
    service: defaultService, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Bitte fülle alle Pflichtfelder aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Anfrage gesendet! Wir melden uns schnellstmöglich.");
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte ruf uns direkt an.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center gap-4 border border-volt/30 bg-[#0a0a0a] p-12 text-center"
        data-testid="contact-success"
      >
        <CheckCircle2 className="h-14 w-14 text-volt" />
        <h3 className="font-heading text-3xl font-extrabold uppercase text-white">Danke!</h3>
        <p className="font-body text-neutral-400 max-w-sm">
          Deine Anfrage ist bei uns eingegangen. Wir melden uns umgehend zurück.
          Bei akuten Notfällen ruf bitte direkt an: <a href={`tel:${SITE.phoneIntl}`} className="text-volt">{SITE.phoneDisplay}</a>
        </p>
      </motion.div>
    );
  }

  const inputCls = "bg-[#0a0a0a] border-white/10 text-white placeholder:text-neutral-600 focus-visible:ring-volt rounded-sm h-12";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Input data-testid="contact-name" placeholder="Name*" value={form.name} onChange={update("name")} className={inputCls} />
        <Input data-testid="contact-phone" placeholder="Telefon*" value={form.phone} onChange={update("phone")} className={inputCls} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input data-testid="contact-email" type="email" placeholder="E-Mail*" value={form.email} onChange={update("email")} className={inputCls} />
        <Input data-testid="contact-vehicle" placeholder="Fahrzeug (Marke / Modell / Baujahr)" value={form.vehicle} onChange={update("vehicle")} className={inputCls} />
      </div>
      <Select value={form.service} onValueChange={update("service")}>
        <SelectTrigger data-testid="contact-service" className={inputCls}>
          <SelectValue placeholder="Leistung wählen" />
        </SelectTrigger>
        <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
          <SelectItem value="Allgemeine Anfrage">Allgemeine Anfrage</SelectItem>
          {SERVICES.map((s) => (
            <SelectItem key={s.slug} value={s.nav}>{s.nav}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea data-testid="contact-message" placeholder="Deine Nachricht*" rows={5} value={form.message} onChange={update("message")} className={`${inputCls} h-auto py-3`} />
      <button
        type="submit"
        data-testid="contact-submit"
        disabled={loading}
        className="group inline-flex w-full items-center justify-center gap-2 bg-volt px-8 py-4 font-body text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.01] disabled:opacity-60 glow-volt"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {loading ? "Wird gesendet…" : "Anfrage senden"}
      </button>
      <p className="font-body text-xs text-neutral-600">
        * Pflichtfelder. Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß Datenschutzerklärung zu.
      </p>
    </form>
  );
}
